import { useEffect, useState, createContext, useContext, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import FestivalPage from "./pages/FestivalPage";
import CompetitionPage from "./pages/CompetitionPage";
import JudgePage from "./pages/JudgePage";
import JudgeLoginPage from "./pages/JudgeLoginPage";
import JudgeScoringPage from "./pages/JudgeScoringPage";
import LiveDisplayPage from "./pages/LiveDisplayPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import PublicResultsPage from "./pages/PublicResultsPage";
import AudienceVotePage from "./pages/AudienceVotePage";
import AudiencePortalPage from "./pages/AudiencePortalPage";
import NowDancingPage from "./pages/NowDancingPage";
import AdminPage from "./pages/AdminPage";
import TermsPage from "./pages/TermsPage";
import EventRegistrationPage from "./pages/EventRegistrationPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import { safeStorage } from "./lib/safeStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Silent backend wake-up - pings backend in background without blocking UI
const useBackendWake = () => {
  const pingedRef = useRef(false);
  useEffect(() => {
    if (pingedRef.current) return;
    pingedRef.current = true;
    const wake = async () => {
      for (let i = 0; i < 15; i++) {
        try {
          await axios.get(`${API}/health`, { timeout: 5000 });
          return; // Backend is awake, start keep-alive
        } catch (e) {
          if (process.env.NODE_ENV === 'development') console.debug('backend waking', i, e.message);
        }
        await new Promise(r => setTimeout(r, 3000));
      }
    };
    wake().then(() => {
      // Keep-alive ping every 2 minutes
      const interval = setInterval(async () => {
        try {
          await axios.get(`${API}/health`, { timeout: 5000 });
        } catch (e) {
          if (process.env.NODE_ENV === 'development') console.debug('keep-alive failed', e.message);
        }
      }, 120000);
      return () => clearInterval(interval);
    });
  }, []);
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(safeStorage.getItem("token"));

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Skip /me entirely when no token is present (user is logged out).
    // This prevents the benign 401 noise on first mount.
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      safeStorage.removeItem("token");
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, { timeout: 10000 });
      const { token: newToken, user: userData } = response.data;
      safeStorage.setItem("token", newToken);
      setToken(newToken);
      setUser(userData);
      return userData;
    } catch (error) {
      // Retry once on network errors (cold start)
      if (!error.response || error.code === 'ECONNABORTED') {
        const retry = await axios.post(`${API}/auth/login`, { email, password }, { timeout: 10000 });
        const { token: newToken, user: userData } = retry.data;
        safeStorage.setItem("token", newToken);
        setToken(newToken);
        setUser(userData);
        return userData;
      }
      throw error;
    }
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    return response.data;
  };

  const logout = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/auth/logout`, {}, { headers });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Logout error:", error);
    }
    safeStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, loginWithGoogle, token }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Handler
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');

      if (sessionId) {
        try {
          // OAuth session exchange — no withCredentials since we use Bearer tokens
          const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
          setUser(response.data.user);
          navigate('/dashboard', { replace: true, state: { user: response.data.user } });
        } catch (error) {
          if (process.env.NODE_ENV === 'development') console.error("Auth callback error:", error);
          toast.error("Authentication failed");
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/dashboard', { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#A1A1AA]">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowChangePassword = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Force password change if admin reset it
  if (user.must_change_password && !allowChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  return children;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/festival/:festivalId" element={<ProtectedRoute><FestivalPage /></ProtectedRoute>} />
      <Route path="/competition/:competitionId" element={<ProtectedRoute><CompetitionPage /></ProtectedRoute>} />
      <Route path="/judge/:judgeId" element={<JudgePage />} />
      <Route path="/judge-login" element={<JudgeLoginPage />} />
      <Route path="/judge-scoring/:judgeId" element={<JudgeScoringPage />} />
      <Route path="/live/:competitionId" element={<LiveDisplayPage />} />
      <Route path="/leaderboard/:competitionId" element={<LeaderboardPage />} />
      <Route path="/results/:competitionId" element={<PublicResultsPage />} />
      <Route path="/vote/:competitionId" element={<AudienceVotePage />} />
      <Route path="/audience" element={<AudiencePortalPage />} />
      <Route path="/now-dancing/:competitionId" element={<NowDancingPage />} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/event/:eventCode" element={<EventRegistrationPage />} />
      <Route path="/change-password" element={<ProtectedRoute allowChangePassword><ChangePasswordPage /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  useBackendWake(); // Silently wakes backend + keep-alive, never blocks UI
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster 
            position="top-right" 
            toastOptions={{
              style: {
                background: '#18181b',
                color: '#FAFAFA',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }
            }}
          />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
